export type Nullable<T> = T | null;

export enum AdminRoles {
  MasterAdmin = 1,
  AEContentAdmin = 2,
  WorkspaceAdmin = 3,
}

export enum WorkspaceRoles {
  WorkspaceAdmin = 3,
  WorkspaceUser = 4,
  WorkspaceViewer = 5,
}

export enum Roles {
  WorkspaceUser = 4,
  WorkspaceViewer = 5,
}

export enum ActionType {
  Login = 'LOGIN',
  Signup = 'SIGNUP',
}

export const roleOptions = [
  { label: 'Workspace Admin', value: AdminRoles.WorkspaceAdmin },
  { label: 'Workspace Contributor', value: Roles.WorkspaceUser },
  { label: 'Workspace Viewer', value: Roles.WorkspaceViewer },
];

export enum BlockType {
  Text = 1,
  Numeric = 2,
  Date = 3,
  Selection = 4,
  Media = 5,
  Radio = 8,
  LongText = 9,
  Checkbox = 10,
  Section = 12,
  Link = 13,
  SectionTitle = 14,
}

export enum SelectionType {
  Single = 'single',
  Multi = 'multi',
}

export enum DateType {
  Date = 'date',
  Datetime = 'datetime',
}

export enum StaticField {
  Name = 's_name',
  Description = 's_description',
  Media = 's_icon',
  IsPublic = 's_is_public',
  CreatedDate = 's_created_date',
  UpdatedDate = 's_updated_date',
}
export interface BlockResponseObject {
  [blockId: string]: any;
}

export interface Block {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_unique_name: string;
  workspace_id: number;
  content_type_id: number;
}

export interface TextBlock extends Block {
  field_type: BlockType.Text;
  properties: {
    maxlength: number;
    minlength: number;
    placeholder?: string;
  };
}

export interface NumberBlock extends Block {
  field_type: BlockType.Numeric;
}

export interface DateBlock extends Block {
  field_type: BlockType.Date;
  properties: {
    dateType: DateType.Date | DateType.Datetime;
    showInCalendarView: boolean;
  };
}

export interface SelectionBlock extends Block {
  field_type: BlockType.Selection;
  properties: {
    selectionType: SelectionType.Single | SelectionType.Multi;
    options: Array<{ label: string; value: string }>;
  };
}

export interface LongTextBlock extends Block {
  field_type: BlockType.LongText;
}

export interface SectionBlock extends Block {
  field_type: BlockType.Section;
}

export interface MediaBlock extends Block {
  field_type: BlockType.Media;
}

export interface CheckboxBlock extends Block {
  field_type: BlockType.Checkbox;
}

export interface LinkBlock extends Block {
  field_type: BlockType.Link;
}

export interface SectionTitleBlock extends Block {
  field_type: BlockType.SectionTitle;
}

export type AnyBlock =
  | SelectionBlock
  | TextBlock
  | NumberBlock
  | DateBlock
  | CheckboxBlock
  | LongTextBlock
  | MediaBlock
  | SectionBlock
  | LinkBlock
  | SectionTitleBlock;

export type InputBlock =
  | SelectionBlock
  | TextBlock
  | NumberBlock
  | DateBlock
  | CheckboxBlock
  | LongTextBlock
  | MediaBlock
  | SectionBlock
  | LinkBlock
  | SectionTitleBlock;

export interface SelectOptionType<T = string> {
  label: string;
  value: T;
}

export interface Row {
  id: number;
  created_at: string;
  updated_at: string;
}

export interface User extends Row {
  email: string;
  first_name: string;
  last_name: string;
  invitation_link: string;
  default_workspace_id: number;
  is_active: boolean;
  role_id: number;
  role_name: string;
  has_payment_methods: boolean;
  is_invitation_sent: boolean;
  config: {
    share_content_email?: boolean;
    join_workspace_email?: boolean;
    comment_email?: boolean;
  };
}

export interface Workspace extends Row {
  name: string;
  description: string;
  stripe_customer_id: Nullable<string>;
  plan_id: number;
  plan_start_date: string;
  plan_end_date: string;
  has_payment_methods: boolean;
  sport_type_id: number;
  is_base: boolean;
  is_public: boolean;
  primary_color: string;
  secondary_color: string;
  logo: string;
}

export interface ExtendedWorkspace extends Workspace {
  role_id: number;
  role_name: string;
}

export interface WorkspaceUser extends Row {
  workspace_id: number;
  user_id: number;
  role_id: number;
  invitation_token: string;
}

export interface WorkspacePayment extends Row {
  workspace_id: number;
  user_id: number;
  payment: number;
}

export interface Plan extends Row {
  name: string;
  stripe_product_id: string;
}

export interface Role {
  id: number;
  name: string;
}

export interface Feature {
  name: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  active: boolean;
  deleted: boolean;
  livemode: boolean;
  object: string;
  package_dimensions: string;
  shippable: boolean;
  statement_descriptor: string;
  tax_code: string;
  type: string;
  unit_label: string;
  url: string;
  marketing_features: Feature[];
  default_price: Price;
  price: Nullable<Price>;
  created: number;
  updated: number;
}

export interface Price {
  id: string;
  active: boolean;
  billing_scheme: string;
  currency: string;
  currency_options: Nullable<string>;
  custom_unit_amount: Nullable<string>;
  deleted: boolean;
  livemode: boolean;
  lookup_key: string;
  metadata: Nullable<object>;
  nickname: string;
  object: string;
  tax_behavior: string;
  tiers: Nullable<string>;
  tiers_mode: '';
  transform_quantity: Nullable<string>;
  type: string;
  unit_amount: number;
  unit_amount_decimal: 'number';
  usage_count: number;
  created: number;
}

export interface Action {
  cnt: number;
  workspace_id: number;
  action_type: string;
  action_date: string;
}

export interface ContentType extends Row {
  name: string;
  description: string;
  icon: string;
  sport_type_id: number;
  workspace_id: number;
  created_by: number;
  is_public: boolean;
  public_link: string;
  reference_id: string;
  workspace_content_type_reference_id: string;
}

export interface Field<T = Record<string, unknown>> extends Row {
  name: string;
  field_type: number;
  workspace_id: number;
  unique_name: string;
  type: number;
  properties: T & {
    minlength: number;
    maxlength: number;
    selectionType: SelectionType.Single | SelectionType.Multi;
    dateType: DateType.Date | DateType.Datetime;
    showInCalendarView: boolean;
  };
}

export interface FieldOption extends Row {
  field_id: number;
  workspace_id: number;
  option_label: string;
  option_value: string;
  is_default: boolean;
  sort_order: number;
}

export interface ContentTypeField extends Row {
  field_id: number;
  workspace_id: number;
  content_type_id: number;
}

export interface ContentTypeFieldView extends Row {
  workspace_id: number;
  content_type_id: number;
  field_id: number;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  properties: any;
  sort_order: number;
  referenceID: string;
  field_options: FieldOption[];
}

export interface Section extends Row {
  name: string;
  description: string;
  duration: number;
  content_type_id: number;
  workspace_id: number;
}

export interface Content extends Row {
  workspace_id: number;
  content_type_id: number;
  is_public: boolean;
  public_link: string;
  content: any;
  status: string;
  reference_id: string;
  name: string;
  description: string;
  icon: string;
  thumbnail: string;
  content_type_name: string;
  media_files: any;
  workspace: string;
  created_user: string;
}

export enum FileType {
  Image = 'image',
  Video = 'video',
}

export interface File {
  type: FileType.Image | FileType.Video;
  src: string;
}

export interface GroupForm {
  name: string;
  contents: string[];
}

export interface SectionForm {
  name: string;
  duration: number | null | string;
  notes: string;
  groups: GroupForm[];
}

export interface SportType {
  id: number;
  name: string;
}

export interface Favorite {
  id: number;
  user_id: number;
  workspace_content_id: number;
  created_id: string;
}

export interface ContentCount {
  content_type_id: number;
  content_type_name: string;
  count: number;
}

export interface WorkspaceContentType extends Row {
  workspace_id: number;
  content_type_id: number;
}

export interface DailogProps {
  isOpen: boolean;
  onClose(): void;
}

export interface Author {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  workspace_id: number;
}

export interface CollectionType extends Row {
  name: string;
  description: string;
  reference_id: string;
  workspace_id: number;
  workspace_reference_id: string;
  public_link: string;
}

export interface CollectionContentType extends Row {
  content_type_id: number;
  workspace_id: number;
  collection_type_id: number;
}

export interface Collection extends Row {
  name: string;
  description: string;
  collection_type_id: number;
  workspace_id: number;
  reference_id: string;
  content: any;
  status: string;
  created_by: number;
  public_link: string;
}

export interface Event extends Row {
  title: string;
  description: string;
  workspace_id: number;
  reference_id: string;
  contents: any;
  status: string;
  created_by: number;
  event_time: string;
  event_date: string;
}

export interface Comment extends Row {
  workspace_content_id: number;
  user_id: number;
  comment: string;
}
